import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { Modal } from "../Modal";
import type { CreditNote } from "../../services/financialService";
import { creditNoteStage, creditStageLabel, CREDIT_STAGE_DOTS } from "./creditNoteRegister";

const when = (value?: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";

/** Prepared → Reviewed → Approved, with who did each step and when. */
function ApprovalSteps({ note }: { note: CreditNote }) {
  const stage = creditNoteStage(note);
  const rejected = stage === "rejected";
  const steps = [
    { label: "Prepared", by: note.prepared_by_name, at: note.created_at, done: true },
    {
      label: "Reviewed",
      by: note.reviewed_by_name,
      at: note.reviewed_at,
      done: ["verified", "approved", "posted"].includes(stage) || Boolean(note.reviewed_by_name),
    },
    {
      label: rejected ? "Rejected" : "Approved",
      by: rejected ? note.rejected_by_name : note.approved_by_name,
      at: rejected ? note.rejected_at : note.approved_at,
      done: rejected || ["approved", "posted"].includes(stage),
    },
  ];
  return (
    <ol className="grid gap-2 sm:grid-cols-3">
      {steps.map((step, index) => (
        <li
          key={step.label}
          className={`rounded-lg border px-3 py-2 ${
            step.done ? (rejected && index === 2 ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50") : "border-gray-200"
          }`}
        >
          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
            <span
              className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${
                step.done ? (rejected && index === 2 ? "bg-red-500 text-white" : "bg-emerald-500 text-white") : "bg-gray-200 text-gray-600"
              }`}
            >
              {step.done ? <Check className="h-3 w-3" /> : index + 1}
            </span>
            {step.label}
          </p>
          <p className="mt-0.5 text-xs text-gray-600">{step.done && step.by ? step.by : "Not yet"}</p>
          {step.done && step.at && <p className="text-[11px] text-gray-400">{when(step.at)}</p>}
        </li>
      ))}
    </ol>
  );
}

/**
 * One credit note in full. What the viewer may do with it (edit, submit,
 * review, approve, return, reject) comes in as `children` and `footer`.
 */
export function CreditNoteDetailsModal({
  note,
  onClose,
  footer,
  children,
}: {
  note: CreditNote | null;
  onClose: () => void;
  footer?: ReactNode;
  children?: ReactNode;
}) {
  if (!note) return null;
  const stage = creditNoteStage(note);

  return (
    <Modal isOpen size="2xl" title={`Credit note ${note.credit_number}`} onClose={onClose} footer={footer}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Credit</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900">UGX {Number(note.amount).toLocaleString()}</p>
            <p className="mt-0.5 text-sm text-gray-600">
              off {note.invoice_number} for {note.student_name}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
            <span className={`h-1.5 w-1.5 rounded-full ${CREDIT_STAGE_DOTS[stage] ?? "bg-gray-400"}`} />
            {creditStageLabel(note)}
          </span>
        </div>

        <ApprovalSteps note={note} />

        <section aria-labelledby="credit-reason-heading">
          <h4 id="credit-reason-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Why the credit</h4>
          <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{note.reason}</p>
        </section>

        {note.review_comments?.trim() && (
          <section aria-labelledby="credit-review-heading">
            <h4 id="credit-review-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Reviewer's comments</h4>
            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{note.review_comments}</p>
          </section>
        )}

        {stage === "returned" && note.return_reason && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Sent back{note.returned_by_name ? ` by ${note.returned_by_name}` : ""}: {note.return_reason}
          </p>
        )}
        {stage === "rejected" && note.rejection_reason && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">Rejected: {note.rejection_reason}</p>
        )}

        {children}
      </div>
    </Modal>
  );
}
